import React, { useState } from 'react';
import { 
  User, Target, Trophy, Share2, Users, 
  Upload, Instagram, Send, Youtube, Facebook, MessageCircle,
  TrendingUp, DollarSign, UserCheck, BarChart3, Plus, Trash2, ExternalLink,
  Calendar, CalendarDays, CalendarRange, Link2, ChevronLeft, ChevronRight, Edit, Save, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

// Month names in Malay
const MONTHS = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];

/**
 * CHANNEL PROFILE - LINEAR-MODERN + LUXURY DESIGN V3
 * Standardized headers with Underads red/orange gradient
 */

// ============================================
// STANDARDIZED SECTION HEADER - Underads Brand Gradient
// ============================================
function SectionHeaderStandard({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
      {/* Subtle glow overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
            <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
          </div>
        </div>
        {action && <div>{action}</div>}
      </div>
    </div>
  );
}

// ============================================
// IB BIOGRAPHY SECTION - V3
// ============================================
export function IBBiographySection({ 
  data, 
  editMode, 
  onDataChange,
  onImageUpload 
}) {
  const handleChange = (field, value) => {
    onDataChange({ ...data, [field]: value });
  };

  return (
    <div className="relative group/section">
      {/* Ambient glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/15 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        {/* Standardized Header with dynamic icon */}
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
          <div className="relative flex items-center gap-3">
            {/* Dynamic icon - shows brand logo if uploaded */}
            {data.brandLogoUrl ? (
              <div className="w-10 h-10 rounded-lg overflow-hidden border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
                <img src={data.brandLogoUrl} alt="Brand" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
                <User size={20} className="text-white" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-white font-heading">IB Biography</h3>
              <p className="text-xs text-[hsl(var(--text-muted))]">Identity & Brand Profile</p>
            </div>
          </div>
        </div>
        
        <div className="p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
            {/* Image Upload Area */}
            <div className="flex flex-row sm:flex-col gap-4 shrink-0">
              <ImageUploadBox
                label="Brand Logo"
                image={data.brandLogoUrl}
                editMode={editMode}
                onUpload={(e) => onImageUpload(e, 'brand')}
                accentColor="brand-red"
              />
              <ImageUploadBox
                label="Profile Photo"
                image={data.personalImageUrl}
                editMode={editMode}
                onUpload={(e) => onImageUpload(e, 'personal')}
                accentColor="emerald"
              />
            </div>

            {/* Fields Grid - Labels in LIGHT GREY */}
            <div className="flex-1 space-y-3">
              <EnhancedDataField 
                label="Full Name"
                value={data.fullName}
                editable={editMode}
                valueClassName="text-base sm:text-xl font-bold font-heading"
                onChange={(val) => handleChange('fullName', val)}
              />
              
              <EnhancedDataField 
                label="Nickname / Brand"
                value={data.nickname}
                editable={editMode}
                onChange={(val) => handleChange('nickname', val)}
              />
              
              <EnhancedDataField 
                label="Target Market"
                value={data.targetMarket}
                editable={editMode}
                onChange={(val) => handleChange('targetMarket', val)}
              />
              
              <EnhancedDataField 
                label="Tagline"
                value={data.tagline ? `"${data.tagline}"` : null}
                editable={editMode}
                valueClassName="italic font-medium"
                onChange={(val) => handleChange('tagline', val)}
                rawValue={data.tagline}
              />
              
              <div className="col-span-2">
                <EnhancedDataField 
                  label="Vision"
                  value={data.vision}
                  editable={editMode}
                  multiline
                  onChange={(val) => handleChange('vision', val)}
                />
              </div>
              
              <div className="col-span-2">
                <EnhancedDataField 
                  label="Mission"
                  value={data.mission}
                  editable={editMode}
                  multiline
                  onChange={(val) => handleChange('mission', val)}
                />
              </div>
              
              <div className="col-span-2">
                <EnhancedDataField 
                  label="Unique Selling Point (USP)"
                  value={data.usp}
                  editable={editMode}
                  multiline
                  valueClassName="font-semibold"
                  onChange={(val) => handleChange('usp', val)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ENHANCED DATA FIELD - Labels in LIGHT GREY
// ============================================
function EnhancedDataField({ 
  label, 
  value, 
  editable, 
  onChange,
  valueClassName,
  multiline = false,
  highlight,
  rawValue
}) {
  const highlightStyles = {
    gold: 'border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.05)]',
    brand: 'border-[hsl(var(--brand-red)/0.3)] bg-[hsl(var(--brand-red)/0.05)]',
  };

  return (
    <div className="space-y-2">
      {/* Label in LIGHT GREY - not red, not white */}
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF]">
        {label}
      </p>
      {editable ? (
        multiline ? (
          <Textarea 
            className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] min-h-[80px] text-white"
            value={rawValue || value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <Input 
            className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] h-11 text-white"
            value={rawValue || value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      ) : (
        <div className={cn(
          'px-4 py-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]',
          'transition-all duration-200 hover:border-[hsl(var(--border-hover))]',
          highlight && highlightStyles[highlight]
        )}>
          <p className={cn(
            'text-white leading-relaxed',
            valueClassName
          )}>
            {value || '—'}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================
// IMAGE UPLOAD BOX
// ============================================
function ImageUploadBox({ label, image, editMode, onUpload, accentColor = 'brand-red' }) {
  const colorStyles = {
    'brand-red': {
      border: 'border-[#E63946]/40',
      bg: 'bg-[#E63946]/10',
      glow: 'shadow-[0_0_30px_-5px_#E63946]'
    },
    'emerald': {
      border: 'border-[hsl(var(--emerald)/0.4)]',
      bg: 'bg-[hsl(var(--emerald)/0.1)]',
      glow: 'shadow-[0_0_30px_-5px_hsl(var(--emerald)/0.5)]'
    },
  };
  const styles = colorStyles[accentColor];

  return (
    <div className="relative group">
      <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">{label}</p>
      <div className={cn(
        'relative w-36 h-36 rounded-xl border',
        'flex items-center justify-center overflow-hidden',
        'transition-all duration-300',
        styles.border, styles.bg,
        image && styles.glow
      )}>
        {image ? (
          <img src={image} alt={label} className="w-full h-full object-cover" />
        ) : (
          <User size={48} className="text-[hsl(var(--text-muted))]" />
        )}
        
        {editMode && (
          <>
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer backdrop-blur-sm">
              <Upload size={24} className="text-white mb-1" />
              <span className="text-xs text-white font-medium">Upload</span>
            </div>
            <input 
              type="file" 
              className="absolute inset-0 opacity-0 cursor-pointer" 
              accept="image/*"
              onChange={onUpload}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// YEARLY TARGETS SECTION - NEW (7 cards matching Monthly Targets)
// ============================================
export function YearlyTargetsSection({ 
  data, 
  editMode, 
  onDataChange,
  selectedYear,
  onYearChange,
  channelId,
  apiData  // New prop - targets from API
}) {
  const currentYear = new Date().getFullYear();
  
  // Use API data if available, otherwise fall back to props data
  const displayData = apiData || data || {};
  
  // Same 7 targets as Monthly Targets
  const targets = [
    { key: 'subscribers', label: 'Subscribers', icon: Users, color: 'sapphire' },
    { key: 'leads', label: 'Leads', icon: Target, color: 'brand-red' },
    { key: 'closed', label: 'Closed', icon: UserCheck, color: 'emerald' },
    { key: 'deposit', label: 'Deposit', icon: DollarSign, color: 'gold', prefix: '$' },
    { key: 'activeClient', label: 'Active Client', icon: Users, color: 'brand-red' },
    { key: 'tradedLot', label: 'Traded Lot', icon: BarChart3, color: 'orange' },
    { key: 'commission', label: 'Commission', icon: TrendingUp, color: 'emerald', prefix: 'RM ' },
  ];

  const colorMap = {
    'brand-red': { text: 'text-[#E63946]', border: 'border-[#E63946]/30', glow: 'hover:shadow-[0_0_25px_-5px_#E63946]' },
    'gold': { text: 'text-[hsl(var(--gold))]', border: 'border-[hsl(var(--gold)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--gold)/0.4)]' },
    'emerald': { text: 'text-[hsl(var(--emerald))]', border: 'border-[hsl(var(--emerald)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--emerald)/0.4)]' },
    'sapphire': { text: 'text-[hsl(var(--sapphire))]', border: 'border-[hsl(var(--sapphire)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--sapphire)/0.4)]' },
    'orange': { text: 'text-[#F97316]', border: 'border-[#F97316]/30', glow: 'hover:shadow-[0_0_25px_-5px_#F97316]' },
  };

  const formatValue = (val) => {
    const num = String(val).replace(/,/g, '');
    if (!isNaN(num) && num !== '') {
      return Number(num).toLocaleString();
    }
    return val || '0';
  };

  return (
    <div className="relative group/section" data-testid="yearly-targets-section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#F59E0B]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        {/* Header with Year Selector */}
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#F59E0B]/20 via-[#10B981]/15 to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/10 to-transparent opacity-50" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#F59E0B]/30 to-[#10B981]/20 border border-[#F59E0B]/40 shadow-[0_0_15px_-3px_#F59E0B]">
                <CalendarRange size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-heading">Yearly Targets</h3>
                <p className="text-xs text-[hsl(var(--text-muted))]">Annual Performance Goals</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Year Selector */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onYearChange && onYearChange((selectedYear || currentYear) - 1)}
                  className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#F59E0B]/10"
                  data-testid="prev-year-btn"
                >
                  <ChevronLeft size={16} />
                </Button>
                <div className="min-w-[80px] text-center">
                  <span className="text-lg font-bold text-white">{selectedYear || currentYear}</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onYearChange && onYearChange((selectedYear || currentYear) + 1)}
                  className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#F59E0B]/10"
                  data-testid="next-year-btn"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {targets.map((item) => {
              const Icon = item.icon;
              const colors = colorMap[item.color];
              const value = displayData[item.key];
              
              return (
                <div 
                  key={item.key}
                  className={cn(
                    'relative bg-[hsl(var(--surface-elevated))] rounded-lg p-4',
                    'border transition-all duration-300',
                    colors.border, colors.glow
                  )}
                >
                  <div className="flex flex-col items-center text-center">
                    <Icon size={18} className={cn(colors.text, 'mb-2')} />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white mb-3">
                      {item.label}
                    </p>
                    {/* Always read-only display */}
                    <p className={cn('font-mono font-bold text-xl', colors.text)}>
                      {item.prefix}{formatValue(value)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MONTHLY TARGETS SECTION - V3 (WITH MONTH NAVIGATION)
// ============================================
export function MonthlyTargetsSection({ 
  data, 
  editMode, 
  onDataChange,
  selectedMonth,
  selectedYear,
  onMonthChange,
  yearlyTargets,
  channelId,
  apiData  // New prop - targets from API
}) {
  const currentDate = new Date();
  const currentMonth = selectedMonth !== undefined ? selectedMonth : currentDate.getMonth();
  const currentYear = selectedYear || currentDate.getFullYear();
  
  // Use API data if available, otherwise fall back to props data
  const displayData = apiData || data || {};
  
  const goToPreviousMonth = () => {
    if (onMonthChange) {
      if (currentMonth === 0) {
        onMonthChange(11, currentYear - 1);
      } else {
        onMonthChange(currentMonth - 1, currentYear);
      }
    }
  };

  const goToNextMonth = () => {
    if (onMonthChange) {
      if (currentMonth === 11) {
        onMonthChange(0, currentYear + 1);
      } else {
        onMonthChange(currentMonth + 1, currentYear);
      }
    }
  };

  const targets = [
    { key: 'subscribers', label: 'Subscribers', icon: Users, color: 'sapphire' },
    { key: 'leads', label: 'Leads', icon: Target, color: 'brand-red' },
    { key: 'closed', label: 'Closed', icon: UserCheck, color: 'emerald' },
    { key: 'deposit', label: 'Deposit', icon: DollarSign, color: 'gold', prefix: '$' },
    { key: 'activeClient', label: 'Active Client', icon: Users, color: 'brand-red' },
    { key: 'tradedLot', label: 'Traded Lot', icon: BarChart3, color: 'orange' },
    { key: 'commission', label: 'Commission', icon: TrendingUp, color: 'emerald', prefix: 'RM ' },
  ];

  const formatValue = (val) => {
    const num = String(val).replace(/,/g, '');
    if (!isNaN(num) && num !== '') {
      return Number(num).toLocaleString();
    }
    return val || '0';
  };

  const colorMap = {
    'brand-red': { text: 'text-[#E63946]', border: 'border-[#E63946]/30', glow: 'hover:shadow-[0_0_25px_-5px_#E63946]' },
    'gold': { text: 'text-[hsl(var(--gold))]', border: 'border-[hsl(var(--gold)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--gold)/0.4)]' },
    'emerald': { text: 'text-[hsl(var(--emerald))]', border: 'border-[hsl(var(--emerald)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--emerald)/0.4)]' },
    'sapphire': { text: 'text-[hsl(var(--sapphire))]', border: 'border-[hsl(var(--sapphire)/0.3)]', glow: 'hover:shadow-[0_0_25px_-5px_hsl(var(--sapphire)/0.4)]' },
    'orange': { text: 'text-[#F97316]', border: 'border-[#F97316]/30', glow: 'hover:shadow-[0_0_25px_-5px_#F97316]' },
  };

  // Calculate progress vs yearly
  const calculateYearlyProgress = () => {
    if (!yearlyTargets) return null;
    const monthlyCommission = parseFloat(String(displayData?.commission || 0).replace(/,/g, ''));
    const yearlyCommission = yearlyTargets.commission || 1;
    return ((monthlyCommission / yearlyCommission) * 100 * 12).toFixed(1);
  };

  const projectedProgress = calculateYearlyProgress();

  return (
    <div className="relative group/section" data-testid="monthly-targets-section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        {/* Header with Month Navigation */}
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
                <Target size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-heading">Monthly Targets</h3>
                <p className="text-xs text-[hsl(var(--text-muted))]">Performance Goals</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Month Navigation */}
              {onMonthChange && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToPreviousMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#E63946]/10"
                    data-testid="prev-month-btn"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <div className="min-w-[140px] text-center">
                    <span className="text-lg font-bold text-white">{MONTHS[currentMonth]}</span>
                    <span className="text-lg text-[#6B7280] ml-2">{currentYear}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToNextMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#E63946]/10"
                    data-testid="next-month-btn"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {/* Progress vs Yearly (if yearlyTargets provided) */}
          {yearlyTargets && projectedProgress && (
            <div className="mb-4 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
              <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1">
                <span>Progress vs Yearly Target</span>
                <span>{projectedProgress}% projected</span>
              </div>
              <div className="h-2 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#E63946] to-[#F97316] rounded-full transition-all"
                  style={{ width: `${Math.min(((currentMonth + 1) / 12) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {targets.map((item) => {
              const Icon = item.icon;
              const colors = colorMap[item.color];
              const value = displayData[item.key];
              
              return (
                <div 
                  key={item.key}
                  className={cn(
                    'relative bg-[hsl(var(--surface-elevated))] rounded-lg p-4',
                    'border transition-all duration-300',
                    colors.border, colors.glow
                  )}
                >
                  <div className="flex flex-col items-center text-center">
                    <Icon size={18} className={cn(colors.text, 'mb-2')} />
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white mb-3">
                      {item.label}
                    </p>
                    {/* Always read-only display */}
                    <p className={cn('font-mono font-bold text-xl', colors.text)}>
                      {item.prefix}{formatValue(value)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MONTHLY MISSIONS SECTION - Activities to achieve goals (WITH MONTH NAVIGATION)
// ============================================
export function MonthlyMissionsSection({ 
  data, 
  editMode, 
  onDataChange,
  selectedMonth,
  selectedYear,
  onMonthChange,
  channelId,
  apiData  // New prop - missions from API
}) {
  const currentDate = new Date();
  const currentMonth = selectedMonth !== undefined ? selectedMonth : currentDate.getMonth();
  const currentYear = selectedYear || currentDate.getFullYear();
  
  // Use API data if available, otherwise fall back to props data
  const missions = apiData || data?.missions || {
    adsContent: 0,
    organicContent: 0,
    webinar: 0,
    meetup: 0,
    live: 0,
    livetrade: 0,
    signal: 0
  };
  
  const goToPreviousMonth = () => {
    if (onMonthChange) {
      if (currentMonth === 0) {
        onMonthChange(11, currentYear - 1);
      } else {
        onMonthChange(currentMonth - 1, currentYear);
      }
    }
  };

  const goToNextMonth = () => {
    if (onMonthChange) {
      if (currentMonth === 11) {
        onMonthChange(0, currentYear + 1);
      } else {
        onMonthChange(currentMonth + 1, currentYear);
      }
    }
  };

  const defaultMissions = [
    { key: 'adsContent', label: 'Ads Content', icon: Target, color: '#E63946', description: 'Paid advertising content pieces' },
    { key: 'organicContent', label: 'Organic Content', icon: Share2, color: '#10B981', description: 'Social media & organic posts' },
    { key: 'webinar', label: 'Webinar', icon: Users, color: '#3B82F6', description: 'Online educational sessions' },
    { key: 'meetup', label: 'Meetup', icon: Users, color: '#8B5CF6', description: 'Physical meetup events' },
    { key: 'live', label: 'Live', icon: Instagram, color: '#EC4899', description: 'Live streaming sessions' },
    { key: 'livetrade', label: 'Live Trade', icon: TrendingUp, color: '#F59E0B', description: 'Live trading demonstrations' },
    { key: 'signal', label: 'Signal', icon: Send, color: '#06B6D4', description: 'Trading signals shared' },
  ];

  return (
    <div className="relative group/section" data-testid="monthly-missions-section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        {/* Header with Month Navigation */}
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#8B5CF6]/20 via-[#EC4899]/15 to-transparent relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/10 to-transparent opacity-50" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#8B5CF6]/30 to-[#EC4899]/20 border border-[#8B5CF6]/40 shadow-[0_0_15px_-3px_#8B5CF6]">
                <CalendarDays size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-heading">Monthly Missions</h3>
                <p className="text-xs text-[hsl(var(--text-muted))]">Activities to achieve targets</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Month Navigation */}
              {onMonthChange && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToPreviousMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#8B5CF6]/10"
                    data-testid="missions-prev-month-btn"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <div className="min-w-[140px] text-center">
                    <span className="text-lg font-bold text-white">{MONTHS[currentMonth]}</span>
                    <span className="text-lg text-[#6B7280] ml-2">{currentYear}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={goToNextMonth}
                    className="h-8 w-8 p-0 border-[#2A2A2E] hover:bg-[#8B5CF6]/10"
                    data-testid="missions-next-month-btn"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {defaultMissions.map((mission) => {
              const Icon = mission.icon;
              const value = missions[mission.key] || 0;
              
              return (
                <div 
                  key={mission.key}
                  className="relative bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border transition-all duration-300 hover:scale-[1.02] group/mission"
                  style={{ 
                    borderColor: `${mission.color}30`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 0 25px -5px ${mission.color}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center mb-2"
                      style={{ backgroundColor: `${mission.color}20` }}
                    >
                      <Icon size={18} style={{ color: mission.color }} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white mb-3">
                      {mission.label}
                    </p>
                    {/* Always read-only display */}
                    <p 
                      className="font-mono font-bold text-xl"
                      style={{ color: mission.color }}
                    >
                      {value}
                    </p>
                  </div>
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#0a0a0c] border border-[hsl(var(--border))] rounded-lg opacity-0 group-hover/mission:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    <p className="text-[10px] text-[#9CA3AF]">{mission.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HIGHEST RECORD SECTION - V3 (Calculates from real data)
// ============================================
export function HighestRecordSection({ data, editMode, onDataChange, historicalData, dailyData }) {
  const metrics = [
    { key: 'subscribers', label: 'Subscribers', icon: Users, dataKey: 'new_subs' },
    { key: 'leads', label: 'Leads', icon: Target, dataKey: 'leads' },
    { key: 'closed', label: 'Closed', icon: UserCheck, dataKey: 'closing' },
    { key: 'deposit', label: 'Deposit', icon: DollarSign, prefix: '$', dataKey: 'total_deposit' },
    { key: 'activeClient', label: 'Active Client', icon: Users, dataKey: 'total_active_client' },
    { key: 'tradedLot', label: 'Traded Lot', icon: BarChart3, dataKey: 'total_traded_lot' },
    { key: 'commission', label: 'Commission', icon: TrendingUp, prefix: 'RM ', dataKey: 'total_commission' },
  ];

  const timeframes = [
    { key: 'daily', label: 'Daily', icon: Calendar, color: 'emerald' },
    { key: 'weekly', label: 'Weekly', icon: CalendarDays, color: 'sapphire' },
    { key: 'monthly', label: 'Monthly', icon: CalendarRange, color: 'gold' },
  ];

  // Calculate highest records from real data
  const calculateHighestRecords = () => {
    const records = {};
    
    // Initialize records structure
    metrics.forEach(m => {
      records[m.key] = {
        daily: { value: '—', date: '—' },
        weekly: { value: '—', date: '—' },
        monthly: { value: '—', date: '—' }
      };
    });
    
    // Combine historical data (2025) and daily data (2026)
    const allData = [];
    
    // Process historical data (from API)
    if (historicalData && Array.isArray(historicalData)) {
      historicalData.forEach(day => {
        allData.push({
          date: day.date,
          subscribers: day.marketing?.new_subs || day.subscribers || 0,
          leads: day.sales?.leads || day.leads || 0,
          closed: day.sales?.closing || day.closing || 0,
          deposit: day.sales?.total_deposit || day.deposit || 0,
          activeClient: day.operation?.total_active_client || day.active_client || 0,
          tradedLot: day.operation?.total_traded_lot || day.traded_lot || 0,
          commission: day.sales?.total_commission || day.commission || 0
        });
      });
    }
    
    // Process daily data from localStorage format
    if (dailyData) {
      // Marketing data
      if (dailyData.marketing && Array.isArray(dailyData.marketing)) {
        dailyData.marketing.forEach(day => {
          const existing = allData.find(d => d.date === day.date);
          if (existing) {
            existing.subscribers = Math.max(existing.subscribers, day.newSubs || 0);
          } else {
            allData.push({
              date: day.date,
              subscribers: day.newSubs || 0,
              leads: 0, closed: 0, deposit: 0, activeClient: 0, tradedLot: 0, commission: 0
            });
          }
        });
      }
      
      // Sales data
      if (dailyData.sales && Array.isArray(dailyData.sales)) {
        // Helper: sum broker field — handles BOTH legacy brokerA-E AND brokers dict (Telegram/WhatsApp recovery format)
        const _sumField = (day, field, usdAlias) => {
          if (day.brokers && typeof day.brokers === 'object') {
            return Object.values(day.brokers).reduce((s, b) => {
              if (!b || typeof b !== 'object') return s;
              let v = b[field];
              if ((v === undefined || v === null) && usdAlias) v = b[usdAlias];
              return s + (Number(v) || 0);
            }, 0);
          }
          return (day.brokerA?.[field] || 0) + (day.brokerB?.[field] || 0) + (day.brokerC?.[field] || 0);
        };
        dailyData.sales.forEach(day => {
          const existing = allData.find(d => d.date === day.date);
          const totalDeposit = _sumField(day, 'deposit', 'deposit_usd');
          const totalCommission = _sumField(day, 'commission', 'commission_usd');
          const totalActive = _sumField(day, 'active');
          const totalLots = _sumField(day, 'lots');
          
          if (existing) {
            existing.leads = Math.max(existing.leads, day.totalLeads || 0);
            existing.closed = Math.max(existing.closed, day.closed || 0);
            existing.deposit = Math.max(existing.deposit, totalDeposit);
            existing.activeClient = Math.max(existing.activeClient, totalActive);
            existing.tradedLot = Math.max(existing.tradedLot, totalLots);
            existing.commission = Math.max(existing.commission, totalCommission);
          } else {
            allData.push({
              date: day.date,
              subscribers: 0,
              leads: day.totalLeads || 0,
              closed: day.closed || 0,
              deposit: totalDeposit,
              activeClient: totalActive,
              tradedLot: totalLots,
              commission: totalCommission
            });
          }
        });
      }
    }
    
    if (allData.length === 0) {
      return records; // Return default empty records
    }
    
    // Sort by date
    allData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate daily highs
    metrics.forEach(metric => {
      let maxDaily = { value: 0, date: '' };
      
      allData.forEach(day => {
        const value = day[metric.key] || 0;
        if (value > maxDaily.value) {
          maxDaily = { value, date: day.date };
        }
      });
      
      if (maxDaily.value > 0) {
        records[metric.key].daily = {
          value: formatNumber(maxDaily.value),
          date: formatDate(maxDaily.date)
        };
      }
    });
    
    // Calculate weekly highs
    const weeklyTotals = {};
    allData.forEach(day => {
      const date = new Date(day.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeklyTotals[weekKey]) {
        weeklyTotals[weekKey] = {};
        metrics.forEach(m => weeklyTotals[weekKey][m.key] = 0);
      }
      
      metrics.forEach(m => {
        weeklyTotals[weekKey][m.key] += day[m.key] || 0;
      });
    });
    
    metrics.forEach(metric => {
      let maxWeekly = { value: 0, week: '' };
      
      Object.entries(weeklyTotals).forEach(([week, totals]) => {
        if (totals[metric.key] > maxWeekly.value) {
          maxWeekly = { value: totals[metric.key], week };
        }
      });
      
      if (maxWeekly.value > 0) {
        const weekDate = new Date(maxWeekly.week);
        const weekNum = Math.ceil((weekDate.getDate()) / 7);
        records[metric.key].weekly = {
          value: formatNumber(maxWeekly.value),
          date: `Week ${weekNum}, ${weekDate.toLocaleString('default', { month: 'short' })} ${weekDate.getFullYear()}`
        };
      }
    });
    
    // Calculate monthly highs
    const monthlyTotals = {};
    allData.forEach(day => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyTotals[monthKey]) {
        monthlyTotals[monthKey] = {};
        metrics.forEach(m => monthlyTotals[monthKey][m.key] = 0);
      }
      
      metrics.forEach(m => {
        monthlyTotals[monthKey][m.key] += day[m.key] || 0;
      });
    });
    
    metrics.forEach(metric => {
      let maxMonthly = { value: 0, month: '' };
      
      Object.entries(monthlyTotals).forEach(([month, totals]) => {
        if (totals[metric.key] > maxMonthly.value) {
          maxMonthly = { value: totals[metric.key], month };
        }
      });
      
      if (maxMonthly.value > 0) {
        const [year, month] = maxMonthly.month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        records[metric.key].monthly = {
          value: formatNumber(maxMonthly.value),
          date: `${monthNames[parseInt(month) - 1]} ${year}`
        };
      }
    });
    
    return records;
  };
  
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return num.toLocaleString();
    return num.toString();
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const recordsData = calculateHighestRecords();
  const hasRealData = historicalData?.length > 0 || dailyData?.marketing?.length > 0 || dailyData?.sales?.length > 0;

  const colorMap = {
    emerald: 'text-[hsl(var(--emerald))] bg-[hsl(var(--emerald)/0.1)] border-[hsl(var(--emerald)/0.3)]',
    sapphire: 'text-[hsl(var(--sapphire))] bg-[hsl(var(--sapphire)/0.1)] border-[hsl(var(--sapphire)/0.3)]',
    gold: 'text-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.1)] border-[hsl(var(--gold)/0.3)]',
  };

  return (
    <div className="relative group/section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <SectionHeaderStandard 
          icon={Trophy}
          title="Highest Record"
          subtitle={hasRealData ? "Calculated from real data" : "No data available yet"}
        />
        
        <div className="p-3 sm:p-6">
          {/* Real Data Badge */}
          {hasRealData && (
            <div className="mb-4 inline-flex items-center px-3 py-1 bg-[hsl(var(--emerald)/0.15)] border border-[hsl(var(--emerald)/0.3)] rounded-full">
              <span className="w-2 h-2 rounded-full bg-[hsl(var(--emerald))] mr-2 animate-pulse" />
              <span className="text-xs font-medium text-[hsl(var(--emerald))]">Dikira dari data sebenar</span>
            </div>
          )}
          
          {/* Timeframe Headers */}
          <div className="grid grid-cols-[100px_1fr_1fr_1fr] sm:grid-cols-[180px_1fr_1fr_1fr] gap-1.5 sm:gap-3 mb-4">
            <div /> {/* Empty cell for metric labels column */}
            {timeframes.map((tf) => {
              const Icon = tf.icon;
              return (
                <div key={tf.key} className={cn('flex items-center justify-center gap-1 sm:gap-2 py-1.5 sm:py-2 rounded-lg border', colorMap[tf.color])}>
                  <Icon size={12} className="sm:w-3.5 sm:h-3.5" />
                  <span className="text-[8px] sm:text-xs font-bold uppercase tracking-wider">{tf.label}</span>
                </div>
              );
            })}
          </div>

          {/* Metrics Grid - CENTERED labels */}
          <div className="space-y-1.5 sm:space-y-2">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              const records = recordsData[metric.key];
              
              return (
                <div key={metric.key} className="grid grid-cols-[100px_1fr_1fr_1fr] sm:grid-cols-[180px_1fr_1fr_1fr] gap-1.5 sm:gap-3">
                  {/* Metric Label - CENTERED */}
                  <div className="flex items-center justify-center gap-1 sm:gap-2 px-1.5 sm:px-3 py-2 rounded-lg bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                    <Icon size={12} className="text-[#9CA3AF] sm:w-3.5 sm:h-3.5 shrink-0" />
                    <span className="text-[10px] sm:text-sm font-medium text-white truncate">{metric.label}</span>
                  </div>
                  
                  <RecordCell 
                    value={records?.daily?.value} 
                    date={records?.daily?.date}
                    prefix={metric.prefix}
                    color="emerald"
                  />
                  <RecordCell 
                    value={records?.weekly?.value} 
                    date={records?.weekly?.date}
                    prefix={metric.prefix}
                    color="sapphire"
                  />
                  <RecordCell 
                    value={records?.monthly?.value} 
                    date={records?.monthly?.date}
                    prefix={metric.prefix}
                    color="gold"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function RecordCell({ value, date, prefix, color }) {
  const colorStyles = {
    emerald: 'hover:border-[hsl(var(--emerald)/0.4)] hover:shadow-[0_0_15px_-5px_hsl(var(--emerald)/0.3)]',
    sapphire: 'hover:border-[hsl(var(--sapphire)/0.4)] hover:shadow-[0_0_15px_-5px_hsl(var(--sapphire)/0.3)]',
    gold: 'hover:border-[hsl(var(--gold)/0.4)] hover:shadow-[0_0_15px_-5px_hsl(var(--gold)/0.3)]',
  };
  const textColor = {
    emerald: 'text-[hsl(var(--emerald))]',
    sapphire: 'text-[hsl(var(--sapphire))]',
    gold: 'text-[hsl(var(--gold))]',
  };

  return (
    <div className={cn(
      'flex flex-col items-center justify-center py-1.5 sm:py-2 px-1 sm:px-3 rounded-lg',
      'bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]',
      'transition-all duration-200 overflow-hidden',
      colorStyles[color]
    )}>
      <p className={cn('font-mono font-bold text-xs sm:text-lg truncate w-full text-center', textColor[color])}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : (value || '—')}
      </p>
      <p className="text-[8px] sm:text-[10px] text-[hsl(var(--text-muted))] truncate w-full text-center">{date || '—'}</p>
    </div>
  );
}

// ============================================
// SOCIAL MEDIA SECTION - V3 (Custom platform input)
// ============================================
export function SocialMediaSection({ platforms, editMode, onPlatformsChange }) {
  const iconMap = {
    'Telegram': Send,
    'Instagram': Instagram,
    'TikTok': MessageCircle,
    'YouTube': Youtube,
    'Facebook': Facebook,
    'WhatsApp': MessageCircle,
    'Twitter': MessageCircle,
    'LinkedIn': MessageCircle,
    'Linktree': Link2,
    'Other': Link2,
  };

  const colorMap = {
    'Telegram': '#26A5E4',
    'Instagram': '#E4405F',
    'TikTok': '#ffffff',
    'YouTube': '#FF0000',
    'Facebook': '#1877F2',
    'WhatsApp': '#25D366',
    'Twitter': '#1DA1F2',
    'LinkedIn': '#0A66C2',
    'Linktree': '#43E55E',
    'Other': '#9CA3AF',
  };

  const handleAdd = () => {
    // Add empty platform - user will type their own
    onPlatformsChange([...platforms, { platform: '', link: '' }]);
  };

  const handleRemove = (index) => {
    const updated = platforms.filter((_, i) => i !== index);
    onPlatformsChange(updated);
  };

  const handleLinkClick = (link, platform) => {
    if (!link) return;
    let url = link;
    if (platform === 'Telegram' && !link.startsWith('http')) {
      url = link.startsWith('@') ? `https://t.me/${link.slice(1)}` : `https://t.me/${link}`;
    } else if (platform === 'Instagram' && !link.startsWith('http')) {
      url = `https://instagram.com/${link.replace('@', '')}`;
    } else if (!link.startsWith('http')) {
      url = `https://${link}`;
    }
    window.open(url, '_blank');
  };

  const getIcon = (platformName) => {
    // Try to match known platforms
    const normalizedName = platformName?.toLowerCase() || '';
    for (const [key, Icon] of Object.entries(iconMap)) {
      if (normalizedName.includes(key.toLowerCase())) {
        return Icon;
      }
    }
    return Link2; // Default icon
  };

  const getColor = (platformName) => {
    const normalizedName = platformName?.toLowerCase() || '';
    for (const [key, color] of Object.entries(colorMap)) {
      if (normalizedName.includes(key.toLowerCase())) {
        return color;
      }
    }
    return '#9CA3AF'; // Default color
  };

  return (
    <div className="relative group/section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 to-transparent rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden h-full">
        <SectionHeaderStandard 
          icon={Share2}
          title="Social Media"
          subtitle="Connected Platforms"
          action={editMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAdd}
              className="border-[#E63946]/50 text-[#E63946] hover:bg-[#E63946]/10"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          )}
        />
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3">
            {platforms.map((platform, idx) => {
              const Icon = getIcon(platform.platform);
              const color = getColor(platform.platform);
              
              return (
                <div 
                  key={idx}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg',
                    'bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]',
                    'transition-all duration-200 hover:border-[hsl(var(--border-hover))]',
                    !editMode && platform.link && 'cursor-pointer hover:bg-[hsl(var(--surface-overlay))]'
                  )}
                  onClick={() => !editMode && platform.link && handleLinkClick(platform.link, platform.platform)}
                >
                  <div 
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon size={18} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {editMode ? (
                      <>
                        {/* Custom platform name input */}
                        <Input 
                          className="h-6 text-[10px] bg-transparent border-0 p-0 focus-visible:ring-0 text-[#9CA3AF] uppercase tracking-wider font-bold mb-1"
                          value={platform.platform}
                          placeholder="Platform name (e.g. Telegram, TikTok 2)"
                          onChange={(e) => {
                            const updated = [...platforms];
                            updated[idx].platform = e.target.value;
                            onPlatformsChange(updated);
                          }}
                        />
                        <Input 
                          className="h-7 text-sm bg-transparent border-0 p-0 focus-visible:ring-0 text-white"
                          value={platform.link}
                          placeholder="Link or username"
                          onChange={(e) => {
                            const updated = [...platforms];
                            updated[idx].link = e.target.value;
                            onPlatformsChange(updated);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9CA3AF]">
                          {platform.platform || 'Unnamed'}
                        </p>
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-white truncate">{platform.link || '—'}</p>
                          {platform.link && <ExternalLink size={12} className="text-[hsl(var(--text-muted))] shrink-0" />}
                        </div>
                      </>
                    )}
                  </div>
                  {editMode && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleRemove(idx); }}
                      className="h-7 w-7 text-[#E63946] hover:bg-[#E63946]/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// TEAM MEMBERS SECTION - V3
// ============================================
export function TeamMembersSection({ members, editMode, onMembersChange }) {
  const handleAdd = () => {
    onMembersChange([...members, { role: '', name: '' }]);
  };

  const handleRemove = (index) => {
    const updated = members.filter((_, i) => i !== index);
    onMembersChange(updated);
  };

  return (
    <div className="relative group/section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 to-transparent rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden h-full">
        <SectionHeaderStandard 
          icon={Users}
          title="Team Members"
          subtitle={`Project Team (${members.length})`}
          action={editMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAdd}
              className="border-[#E63946]/50 text-[#E63946] hover:bg-[#E63946]/10"
            >
              <Plus size={14} className="mr-1" /> Add
            </Button>
          )}
        />
        
        <div className="p-4">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {members.map((member, idx) => (
              <div 
                key={idx}
                className={cn(
                  'relative p-3 rounded-lg text-center group/member',
                  'bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]',
                  'transition-all duration-200 hover:border-[#E63946]/30',
                  'hover:shadow-[0_0_15px_-5px_#E63946]'
                )}
              >
                {editMode && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(idx)}
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[#E63946] text-white hover:bg-[#E63946]/80 opacity-0 group-hover/member:opacity-100 transition-opacity"
                  >
                    <Trash2 size={10} />
                  </Button>
                )}
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--surface-overlay))] flex items-center justify-center mx-auto mb-2 border border-[hsl(var(--border))]">
                  <User size={18} className="text-[hsl(var(--text-muted))]" />
                </div>
                {editMode ? (
                  <>
                    <Input 
                      className="h-6 text-[9px] text-center bg-transparent border-0 p-0 mb-1 text-[#9CA3AF] uppercase tracking-wider font-bold"
                      value={member.role}
                      placeholder="Role"
                      onChange={(e) => {
                        const updated = [...members];
                        updated[idx].role = e.target.value;
                        onMembersChange(updated);
                      }}
                    />
                    <Input 
                      className="h-6 text-xs text-center bg-transparent border-0 p-0 text-white font-medium"
                      value={member.name}
                      placeholder="Name"
                      onChange={(e) => {
                        const updated = [...members];
                        updated[idx].name = e.target.value;
                        onMembersChange(updated);
                      }}
                    />
                  </>
                ) : (
                  <>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-[#E63946] mb-1">
                      {member.role || 'No Role'}
                    </p>
                    <p className="text-xs text-white font-medium truncate">
                      {member.name || '—'}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// BROKER SECTION - Shows which brokers IB is working with
// ============================================
export function BrokerSection({ data, editMode, onDataChange }) {
  // Available brokers with brand colors
  const availableBrokers = [
    { id: 'hfm', name: 'HFM', color: '#E63946', bgColor: '#E63946' },
    { id: 'octa', name: 'OCTA', color: '#00A650', bgColor: '#00A650' },
    { id: 'vantage', name: 'VANTAGE', color: '#0052FF', bgColor: '#0052FF' },
    { id: 'valetax', name: 'VALETAX', color: '#6B21A8', bgColor: '#6B21A8' },
    { id: 'moneta', name: 'MONETA', color: '#F59E0B', bgColor: '#F59E0B' },
    { id: 'monaxa', name: 'MONAXA', color: '#10B981', bgColor: '#10B981' },
    { id: 'pepperstone', name: 'PEPPERSTONE', color: '#1E3A8A', bgColor: '#1E3A8A' },
  ];

  const activeBrokers = data?.brokers || ['hfm', 'octa'];

  const toggleBroker = (brokerId) => {
    if (activeBrokers.includes(brokerId)) {
      onDataChange({ ...data, brokers: activeBrokers.filter(b => b !== brokerId) });
    } else {
      onDataChange({ ...data, brokers: [...activeBrokers, brokerId] });
    }
  };

  return (
    <div className="relative group/section">
      <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
      
      <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
        <SectionHeaderStandard 
          icon={Link2}
          title="Broker Partners"
          subtitle="Active broker affiliations"
        />
        
        <div className="p-6">
          {editMode ? (
            <div className="space-y-4">
              <p className="text-xs text-[#9CA3AF]">Select active brokers:</p>
              <div className="flex flex-wrap gap-3">
                {availableBrokers.map((broker) => {
                  const isActive = activeBrokers.includes(broker.id);
                  return (
                    <button
                      key={broker.id}
                      onClick={() => toggleBroker(broker.id)}
                      className={cn(
                        'px-5 py-3 rounded-xl border-2 transition-all duration-300 font-bold text-sm tracking-wide',
                        isActive 
                          ? 'border-white/50 scale-105 shadow-lg' 
                          : 'border-[hsl(var(--border))] opacity-40 hover:opacity-70'
                      )}
                      style={{ 
                        backgroundColor: isActive ? `${broker.bgColor}30` : 'transparent',
                        color: isActive ? broker.color : '#6B7280',
                        boxShadow: isActive ? `0 0 20px -5px ${broker.color}` : 'none'
                      }}
                    >
                      {broker.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {activeBrokers.length === 0 ? (
                <p className="text-[#6B7280] text-sm">No brokers configured</p>
              ) : (
                activeBrokers.map((brokerId) => {
                  const broker = availableBrokers.find(b => b.id === brokerId);
                  if (!broker) return null;
                  return (
                    <div 
                      key={broker.id}
                      className="group/broker relative px-6 py-4 rounded-xl border transition-all duration-300 hover:scale-105"
                      style={{ 
                        backgroundColor: `${broker.bgColor}15`,
                        borderColor: `${broker.color}40`,
                        boxShadow: `0 0 25px -8px ${broker.color}`
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-black text-white text-sm"
                          style={{ backgroundColor: broker.bgColor }}
                        >
                          {broker.name.substring(0, 2)}
                        </div>
                        <span 
                          className="font-bold text-base tracking-wide"
                          style={{ color: broker.color }}
                        >
                          {broker.name}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
